import type { ComponentType } from 'react';

import type { IconProps } from '@chakra-ui/react';

import { BriefCaseIcon, ChartUpIcon, PercentIcon, SchoolIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

export type ImpactDrivenStatTheme = 'blue700' | 'blue500' | 'purple700' | 'purple500';

export type ImpactDrivenStat = {
  id: string;
  theme: ImpactDrivenStatTheme;
  /** Numeric portion rendered at the larger type size. */
  value: string;
  /** Unit/suffix rendered at the smaller type size (e.g. "%", "M+", "k+"). */
  valueSuffix: string;
  caption: string;
  /**
   * Optional disclaimer marker rendered as a small superscript next to the caption
   * (e.g. "*"), pointing at the section footnote below the stat cards.
   */
  disclaimer?: string;
  /**
   * Optional Chakra icon. When `undefined` the slot renders an empty layout-preserving
   * spacer so the design's spacing stays intact even for icons we couldn't source from
   * `ab_plp_redesign_2025/icons.tsx` (deferred to the user per the handoff).
   */
  Icon?: ComponentType<IconProps>;
};

export type ImpactDrivenCountry = {
  id: string;
  /** Full country name — always announced to assistive tech and shown on desktop. */
  label: string;
  /**
   * Optional short label shown only at the mobile breakpoint when Figma abbreviates
   * the name (e.g. "United States" → "USA"). Assistive tech still hears `label`;
   * `shortLabel` only swaps the visual text. Pulled verbatim from Figma — case is
   * irrelevant since the label renders `textTransform='uppercase'`.
   */
  shortLabel?: string;
  flagSrc: string;
  flagAlt: string;
};

export const impactDrivenSolutionsContent = {
  pretitle: 'Impact-driven Solutions',
  heading: 'Building a tech-empowered generation ready for the future of innovation',
  intro:
    'Empowering millions of learners with the digital fluency and technical skills needed to expand opportunity, strengthen workforces, and support long-term national growth.',
  trustedByHeading: 'Trusted by governments and public sector partners',
  // Per user decision: the mobile Figma frame duplicates the gradient-panel heading here as
  // an apparent placeholder leftover. We ship "Our global footprint" on both breakpoints to
  // match the desktop intent.
  globalFootprintHeading: 'Our global footprint',
  globalFootprintIntro:
    'Delivering scalable tech education programs across regions, markets, and public sector contexts.',
  logoLockupAlt: 'Logos of governments and public sector partners trusted by Udacity',
  // Footnote rendered below the stat cards. The leading "*" is added at render time and
  // ties back to the asterisks on the disclaimer-marked stats.
  statsFootnote: 'Based on data collected from 26,662 scholarship graduates between August 2021 and December 2025',
};

export const impactDrivenSolutionsStats: ImpactDrivenStat[] = [
  {
    id: 'career-outcome',
    theme: 'blue700',
    value: '86',
    valueSuffix: '%',
    caption: 'Reported a positive career outcome',
    disclaimer: '*',
    Icon: ChartUpIcon,
  },
  {
    id: 'employed',
    theme: 'blue500',
    value: '58',
    valueSuffix: '%',
    caption: 'Unemployed learners got employed',
    disclaimer: '*',
    Icon: BriefCaseIcon,
  },
  {
    id: 'foundational',
    theme: 'purple700',
    value: '4.0',
    valueSuffix: 'M+',
    caption: 'learners in foundational tech programs',
    Icon: SchoolIcon,
  },
  {
    id: 'advanced',
    theme: 'purple500',
    value: '200',
    valueSuffix: 'k+',
    caption: 'learners in advanced paths and expert-led models',
    Icon: PercentIcon,
  },
];

const FLAG_BASE = '/images/government/impact-driven-solutions';

// Country order DIVERGES from the desktop Figma frame at the user's request: long
// names were clustered on Figma's row 2, so we rebalance them across both desktop
// rows (5 cols → 2 rows of 5) so each row carries ~3 long + 2 short names and the
// two longest ("United Arab Emirates" and "European Union") are not adjacent within
// a row. `shortLabel` is populated only for the three countries the mobile Figma
// frame abbreviates (verbatim from nodes 159:25315, 159:25323, 159:25340) so the
// mobile 4-col layout doesn't crowd either.
export const impactDrivenSolutionsCountries: ImpactDrivenCountry[] = [
  {
    id: 'usa',
    label: 'United States',
    shortLabel: 'USA',
    flagSrc: `${FLAG_BASE}/flag-united-states.svg`,
    flagAlt: '',
  },
  { id: 'chile', label: 'Chile', flagSrc: `${FLAG_BASE}/flag-chile.svg`, flagAlt: '' },
  { id: 'saudi-arabia', label: 'Saudi Arabia', flagSrc: `${FLAG_BASE}/flag-saudi-arabia.svg`, flagAlt: '' },
  { id: 'jordan', label: 'Jordan', flagSrc: `${FLAG_BASE}/flag-jordan.svg`, flagAlt: '' },
  {
    id: 'uae',
    label: 'United Arab Emirates',
    shortLabel: 'UAE',
    flagSrc: `${FLAG_BASE}/flag-united-arab-emirates.svg`,
    flagAlt: '',
  },
  {
    id: 'eu',
    label: 'European Union',
    shortLabel: 'EU',
    flagSrc: `${FLAG_BASE}/flag-european-union.svg`,
    flagAlt: '',
  },
  { id: 'oman', label: 'Oman', flagSrc: `${FLAG_BASE}/flag-oman.svg`, flagAlt: '' },
  { id: 'uzbekistan', label: 'Uzbekistan', flagSrc: `${FLAG_BASE}/flag-uzbekistan.svg`, flagAlt: '' },
  { id: 'egypt', label: 'Egypt', flagSrc: `${FLAG_BASE}/flag-egypt.svg`, flagAlt: '' },
  { id: 'mozambique', label: 'Mozambique', flagSrc: `${FLAG_BASE}/flag-mozambique.svg`, flagAlt: '' },
];

export const impactDrivenSolutionsLockupAssets = {
  desktopSrc: '/images/government/impact-driven-solutions/logo-lockup-desktop.png',
  mobileSrc: '/images/government/impact-driven-solutions/logo-lockup-mobile.png',
};

// Stripes overlay reuses the existing societal-impact asset; same diagonal stripe
// pattern is used as the decorative overlay in both gov sections.
export const impactDrivenSolutionsStripesSrc = '/images/government/societal-impact/stripes.svg';
